import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, Scroll, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { QuoteStepBase } from './pages/quote-step-base/quote-step-base';
import { AuthenticationService } from './services/authentication/authentication.service';
import { InvalidTokenEventService } from './services/authentication/invalid-token-event.service';
import { ModalEventService } from './services/modal-service/modal-event.service';
import { LocalStoreManager } from './services/local-store-manager/local-store-manager.service';
import { BearerTokenKeyName } from './constants/strings';
import { MaskScreenService } from './services/mask-screen-service/mask-screen.service';
import { ToastNotificationService } from './services/toast-notification/toast-notification.service';
import { AuthType } from './models/enums/auth-type';
import { HeaderComponent } from './components/header/header.component';
import { NgClass } from '@angular/common';
import { DropdownEventDirective } from './directives/dropdown-event.directive';
import { UnloadBrowserNotificationDirective } from './directives/unload-browser-notification.directive';
import { FooterComponent } from './components/footer/footer.component';
import { SpinnerLoaderService } from './services/spinner-loader/spinner-loader.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [HeaderComponent, NgClass, DropdownEventDirective, RouterOutlet, UnloadBrowserNotificationDirective, FooterComponent]
})
export class AppComponent extends QuoteStepBase implements OnInit, OnDestroy {
    public masked: boolean = false;
    public hidingMask: boolean = false;
    public routerUrl: string = '';

    private readonly subSink: Subscription = new Subscription();

    constructor(public override injector: Injector,
        private readonly router: Router,
        private readonly invalidTokenEventService: InvalidTokenEventService,
        private readonly authenticationService: AuthenticationService,
        private readonly modalEventService: ModalEventService,
        private readonly maskScreenService: MaskScreenService,
        private readonly localStoreManager: LocalStoreManager,
        public toastNotificationService: ToastNotificationService,
        private readonly spinnerLoaderService: SpinnerLoaderService
    ) {
        super(injector);
    }

    public ngOnInit(): void {
        this.createForm();
        this.monitorInvalidTokenEvents();
        this.monitorNavigationEndEvents();
        this.monitorModalEvents();
        this.monitorMaskEvents();
        this.monitorPopstate();
        this.monitorLogin();
    }

    public ngOnDestroy(): void {
        this.localStoreManager.deleteData(BearerTokenKeyName);
        this.subSink?.unsubscribe();
    }

    public onMaskClick() {
        this.maskScreenService.maskScreen(false);
    }

    private monitorLogin() {
        this.authenticationService.onLogin().subscribe((loginType: AuthType | undefined) => {
            if (loginType === AuthType.External) {
                (globalThis as any).Twilio.initWebchat();
                (globalThis as any).Twilio?.openWebchatFAQs('Motor')
            }
        })
    }

    private monitorModalEvents() {
        this.subSink.add(
            this.modalEventService.onOpenModalRequest()
                .subscribe(async event => {
                    if (event?.component) {
                        await this.modalService.openModalComponent(event.component, event.inputs, event.inProcess, event.important, event.queue);
                        this.loadingIndicatorService.hide();
                    }
                })
        );
    }

    private monitorMaskEvents() {
        this.subSink.add(this.maskScreenService.onMaskChange().subscribe(masking => {
            if (!masking) {
                this.hidingMask = true;
                setTimeout(() => {
                    this.masked = masking;
                    this.hidingMask = false;
                }, 0);
            } else {
                this.masked = masking;
            }
        }));
    }

    private monitorNavigationEndEvents(): void {
        this.subSink.add(
            this.router.events.subscribe(async (r) => {
                if (r instanceof NavigationEnd) {
                    this.routerUrl = r.url;
                    if (this.routerUrl == "/") {
                        await this.router.navigateByUrl('select-product');
                    }
                    this.loadingIndicatorService.hide();
                }

                if (r instanceof Scroll && r.routerEvent instanceof NavigationEnd) {
                    this.spinnerLoaderService.hide();
                    this.googleTagManagerService.track(this.routerUrl);
                }
            })
        );
    }

    private monitorInvalidTokenEvents(): void {
        this.subSink.add(
            this.invalidTokenEventService.onProcessInvalidToken()
                .subscribe(async () => {
                    await this.authenticationService.processInvalidSession();
                })
        );
    }

    /**
    * Creates the initial Quote form if required and adds it to the motorQuoteStateService 
    */
    private createForm(): void {
        this.formBuilderService.getInitialQuoteForm();
        this.loadingIndicatorService.hide()
    }

    /**
     * TRAIL-1996 Using popState to block browser forward navigation to avoid bypassing validation
     * https://stackoverflow.com/questions/11548558/html5-history-disabling-forward-button
     */
    private monitorPopstate(): void {
        //setup the popstate EventListener that reacts to browser history events
        window.addEventListener("popstate", function (event) {
            // In order to remove any "forward"-history (i.e. disable forward 
            // button), this popstate's event history state (having been navigated 
            // back to) must be insert _again_ as a new history state, thereby 
            // making it the new most forwad history state. 
            // This leaves the problem that to have this popstate event's history
            // state to become the new top, it would now be multiple in the history
            //
            // Effectively history would be:
            //  * [states before..] ->
            //  * [popstate's event.state] -> 
            //  * [*newly pushed _duplicate_ of popstate's event.state ]
            // 
            // To remove the annoyance/confusion for the user to have duplicate
            // history states, meaning it has to be clicked at least twice to go 
            // back, we pursue the strategy of marking the current history state
            // as "obsolete", as it has been inserted _again_ as to be the most
            // forward history entry. 
            // 
            // the popstate EventListener will hence have to distinguish 2 cases:
            //
            // case A) "popstate event is _not_ an obsolete duplicate"...
            if (typeof event.state == "object"
                && event.state?.obsolete !== true) {
                //...so we _firstly_ mark this state as to from now on "obsolete",
                // which can be done using the history API's replaceState method
                history.replaceState({ "obsolete": true }, "");
                // and _secondly_ push this state _one_more_time_ to the history
                // which will solve the OP's desired "disable forward button" issue
                history.pushState(event.state, "");
            }
        }, false);
    }
}
